﻿/*Utilities*/
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useUser } from '../../context/UserContext';
/*Components*/
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import ProducerIDsDropdown from '../Global/ProducerIDsDropdown/ProducerIDsDropdown';

import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

/*Images*/
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
/*Style*/
import './IQumulateAgentValidation.scss';
/*APIs*/
import { GetStateList } from '../../api/apppackage';
import { CheckAgentValidation } from '../../api/fast';

export default function IQumulateAgentValidation({ ...props }) {
  const user = useUser();
  const [states, setStates] = useState([]);
  const [producerID, setProducerID] = useState('');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('');
  let policyID = 'test';
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [validating, setValidating] = useState(false);
  let validateBtnLabel = 'Check Agent License';
  const [effectiveDate, setEffectiveDate] = useState(dayjs());

  const handleOpen = () => {
    handleSnackbarClose();
    setOpen(true);
    if (user.hasMultipleProducerIDs === true && producerID.length === 0) {
      setProducerID(user.producerID);
    }
  };
  const handleClose = () => {
    setOpen(false);
    setState('');
    setProducerID('');
  };

  const getContractStates = useQuery({
    queryKey: ['GetStateList'],
    queryFn: async () => await GetStateList()(),
  });

  // Handle getContractStates success with useEffect
  useEffect(() => {
    if (getContractStates.isSuccess && Array.isArray(getContractStates.data)) {
      setStates(getContractStates.data.filter((st) => st.value !== 'CA' && st.value !== 'NY'));
    }
  }, [getContractStates.isSuccess, getContractStates.data]);

  const checkAgentValidation = useMutation({
    mutationFn: async (data) => {
      const validate = CheckAgentValidation(data);
      return await validate();
    },
  });

  // Handle checkAgentValidation success with useEffect
  useEffect(() => {
    if (checkAgentValidation.isSuccess) {
      const data = checkAgentValidation.data;
      setValidating(false);

      if (!data || data.error) {
        setAlertTitle('Unable to Validate License');
        setAlertType('error');
        setAlertMessage(
          'There was an error validating your license or appointment status. Please try again. You should contact Baltimore Life for additional information.'
        );
        handleClose();
        return;
      }

      if (
        data.isAgentLicensed === false ||
        data.trainingNotComplete === true ||
        data.needsAppointment === true ||
        data.productInvalid === true ||
        data.isResidentStatePopulated === false
      ) {
        setAlertTitle('License Validation Error: ');
        setAlertType('error');

        let message = '';

        if (data.isAgentLicensed === false)
          message +=
            'Baltimore Life records indicate that you are not licensed to sell products in this state.\n';

        if (data.trainingNotComplete === true)
          message += 'Unable to create application due to training requirements.\n';

        if (data.needsAppointment === true)
          message += 'You have no appointments registered in this state.\n';

        if (data.productInvalid === true) message += "You're not licensed for this product.\n";

        if (data.isResidentStatePopulated === false) message += 'No resident state license.\n';

        if (message.length > 0)
          message += 'You should contact Baltimore Life for additional information.\n';

        setAlertMessage(message);
      } else {
        setAlertTitle('Ready to Sell in Contract State Selected');
        setAlertMessage('');
        setAlertType('success');
      }

      handleClose();
    }
  }, [checkAgentValidation.isSuccess, checkAgentValidation.data]);

  // Handle checkAgentValidation failure (e.g. non-2xx response) with useEffect
  useEffect(() => {
    if (checkAgentValidation.isError) {
      setValidating(false);
      setAlertTitle('Unable to Validate License');
      setAlertType('error');
      setAlertMessage(
        'There was an error validating your license or appointment status. Please try again. You should contact Baltimore Life for additional information.'
      );
      handleClose();
    }
  }, [checkAgentValidation.isError]);

  const handleChange = (event) => {
    setState(event.target.value);
  };

  const handleSubmit = () => {
    setValidating(true);
    checkAgentValidation.mutate({
      Product: props.productID,
      ProducerID: user.isHO || user.hasMultipleProducerIDs === true ? producerID : user.producerID,
      State: state,
      PolicyNumber: policyID,
      EffectiveDate: effectiveDate,
    });
  };

  const handleInputChange = (event) => {
    setProducerID(event.target.value);
  };

  const handleSnackbarClose = () => {
    setAlertMessage('');
    setAlertTitle('');
  };

  function EffectiveDatePicker() {
    return (
      <div style={{ margin: '1em 0' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Application Date"
            sx={{ width: '100%' }}
            value={effectiveDate}
            onChange={(newValue) => setEffectiveDate(newValue)}
          />
        </LocalizationProvider>
      </div>
    );
  }

  const snackBarCloseBtn = (
    <div className="closeBtn">
      <IconButton aria-label="closePrompt" onClick={handleSnackbarClose}>
        <CancelIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <Button variant="contained" startIcon={<FactCheckIcon />} onClick={handleOpen}>
        {validateBtnLabel}
      </Button>
      {alertTitle.length > 0 && (
        <Snackbar
          action={snackBarCloseBtn}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={alertTitle.length > 0}
          key={'top-center'}
          autoHideDuration={5000}
          className="iqumulate-snackbar"
          sx={{
            width: 'auto',
            boxShadow: 'rgba(0, 0, 0, 0.4) 5px 5px',
            border: '1px solid #000',
            borderRadius: '0.5em',
          }}
        >
          <Alert
            sx={{
              width: '100%',
              '.MuiAlert-icon': {
                width: '1.5em',
              },
            }}
            className="iqumulate-alert"
            severity={alertType}
            onClose={handleSnackbarClose}
            action={snackBarCloseBtn}
          >
            <div className="iqumulate-titleHeader">{alertTitle}</div>
            <div className="iqumulate-description">{alertMessage}</div>
          </Alert>
        </Snackbar>
      )}
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title-AgentValidate">
        <Box className="validationModal">
          <div className="closeBtn">
            <IconButton aria-label="closePrompt" onClick={handleClose}>
              <CancelIcon />
            </IconButton>
          </div>

          <Typography
            id="modal-modal-title-AgentValidate"
            variant="h5"
            component="h3"
            className="titleHeader"
          >
            {validateBtnLabel}
          </Typography>

          <Stack direction="row" spacing={1}>
            {user.hasMultipleProducerIDs === true ? (
              <ProducerIDsDropdown
                setProducerID={(id) => {
                  setProducerID(id);
                }}
                variant="outlined"
                formControlStyle={{ padding: 0 }}
              />
            ) : (
              <TextField
                id="outlined-basic-producerID-AgentValidate"
                label="ProducerID"
                variant="outlined"
                disabled={!user.isHO}
                value={user.isHO ? producerID : user.producerID}
                onChange={handleInputChange}
              />
            )}
            <TextField
              id="outlined-basic-npnID-AgentValidate"
              label="NPN"
              variant="outlined"
              value={user.npnID ?? ''}
              disabled={true}
            />
          </Stack>

          {(getContractStates.isFetching || getContractStates.isPending) && (
            <Box className="contractStatesBox">
              <CircularProgress />
            </Box>
          )}

          {getContractStates.data != null && states.length > 0 && (
            <Box className="contractStatesBox">
              <FormControl fullWidth>
                <InputLabel id="input-label-states-AgentValidate">Contract States</InputLabel>
                <Select
                  labelId="label-stateDropdown-AgentValidate"
                  id="select-stateDropdown-AgentValidate"
                  value={state}
                  label="Contract State"
                  onChange={handleChange}
                  key={'state-select-AgentValidate'}
                >
                  {states.map((p) => (
                    <MenuItem className="menuItem" key={p.value} value={p.value}>
                      {p.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {user.isHO && <EffectiveDatePicker />}
              <div className="submitBtn">
                {checkAgentValidation.isPending || validating ? (
                  <CircularProgress />
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleSubmit}
                    disabled={
                      user.isHO || user.hasMultipleProducerIDs === true
                        ? producerID.length === 0 || state.length === 0
                        : state.length === 0
                    }
                  >
                    Submit
                  </Button>
                )}
              </div>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
}
